import homePageConfig from '../../../_home/_browser-2-0-rebrand/__config/page-configuration/default.js';
import HeroCallToAction from '../components/HeroCallToAction.svelte';

/**
 * Hero CTA stack: logo, title (accessibility), subtitle, button, subtext.
 * @typedef {object} GiveawayHeroCallToActionContent
 * @property {{ alt: string; sources: Array<{ srcset: string }> }} abcmouseLogoSweepstakes
 * @property {typeof import('../components/HeroCallToAction.svelte').default} component - Hero UI implementation for this offer variant.
 * @property {string} ctaButtonText
 * @property {string} ctaButtonTrackId - `data-track-id` on the CTA.
 * @property {string} subtext - May include HTML.
 * @property {string} subtitle
 * @property {string} title - May include HTML.
 */

/**
 * Footer-adjacent legal copy and squiggle transition before NMHP footer.
 * @typedef {object} GiveawayDisclaimerContent
 * @property {{ base: string }} bottomBgImg - White squiggle up (`ComponentBackgroundImage` sources).
 * @property {{ base: string }} bottomBlueBgImg - Blue squiggle (`ComponentBackgroundImage` sources).
 * @property {string} disclaimerParagraph1 - May include HTML.
 * @property {string} disclaimerParagraph2 - May include HTML (e.g. Official Rules link).
 */

/**
 * Bottom subscribe CTA (NMHP JoinCTA parity; disclaimers live on `disclaimer`).
 * @typedef {object} GiveawayJoinCtaContent
 * @property {string} ctaButtonText
 * @property {string} ctaButtonTrackId - `data-track-id` on the CTA.
 */

/**
 * Premium value props grid (NMHP parity; no CTA in giveaway).
 * @typedef {object} GiveawayPremiumValuePropsContent
 * @property {Array<{ img: { alt: string; sources: Array<{ srcset: string }> }; text: string }>} items
 * @property {string} title
 */

/**
 * Sweepstakes section background only (`ComponentBackgroundImage` sources).
 * @typedef {object} GiveawaySweepstakesContent
 * @property {{ base?: string; sm?: string; md?: string; lg?: string; xl?: string }} sweepstakesBg - URLs for `ComponentBackgroundImage` (base below 640px; optional sm 640+; md 1200+; lg 1440+). Omit sm when the small raster matches base.
 */

/**
 * Subject labels for the deep-dive banner below the hero (NMHP parity).
 * @typedef {object} GiveawaySubjectsBannerContent
 * @property {string[]} subjects
 */

/**
 * Learning Meets Adventure logo + copy between subjects banner and premium value props.
 * @typedef {object} GiveawayLearningMeetsAdventureContent
 * @property {{ alt: string; sources: Array<{ srcset: string }> }} learningMeetsAdventureLogo
 * @property {string} paragraph
 * @property {{ base: string }} topBgImg - White squiggle up (`ComponentBackgroundImage` sources).
 */

/**
 * @typedef {object} GiveawayOfferSpecificsParagraphettesBody
 * @property {string[]} items - HTML strings; use `<strong>` for bold segments.
 * @property {'paragraphettes'} type
 */

/**
 * @typedef {object} GiveawayOfferSpecificsParagraphBulletsBody
 * @property {'paragraphBullets'} type
 * @property {string} paragraph
 * @property {string[]} bullets - HTML strings (links, `<strong>`, conditional `<br>`).
 */

/**
 * @typedef {object} GiveawayOfferSpecificsParagraphOnlyBody
 * @property {'paragraph'} type
 * @property {string} text
 */

/**
 * Giveaway offer details: title, subtitle, three rows (image + headline + typed body).
 * @typedef {object} GiveawayOfferSpecificsContent
 * @property {Array<{
 *   body: GiveawayOfferSpecificsParagraphettesBody | GiveawayOfferSpecificsParagraphBulletsBody | GiveawayOfferSpecificsParagraphOnlyBody;
 *   headline: string;
 *   img: { alt: string; sources: Array<{ srcset: string }> };
 *   imgClass: string;
 * }>} items
 * @property {string} subtitle - May include HTML.
 * @property {string} title - May include HTML.
 */

/**
 * Header nav `data-track-id` values for `marketing.page_link_click` (`link_info.link_id`).
 * @typedef {object} GiveawayHeaderLinkTrackIds
 * @property {string} giveAGift
 * @property {string} login
 * @property {string} logout
 * @property {string} subscribe - Subscribe to Enter (sign up) in header bar
 */

/**
 * Sweepstakes: Disneyland trip / family vacation 2026
 * @typedef {object} FamilyVacation2026Config
 * @property {GiveawayDisclaimerContent} disclaimer
 * @property {object} footer - NMHP footer: shared reference to home page config footer (read-only in app code).
 * @property {GiveawayHeaderLinkTrackIds} headerLinkTrackIds
 * @property {GiveawayHeroCallToActionContent} heroCallToAction
 * @property {GiveawayJoinCtaContent} joinCta
 * @property {GiveawayLearningMeetsAdventureContent} learningMeetsAdventure
 * @property {import('$lib/utils/page').PageMetadata} metadata
 * @property {GiveawayOfferSpecificsContent} offerSpecifics
 * @property {GiveawayPremiumValuePropsContent} premiumValueProps
 * @property {GiveawaySubjectsBannerContent} subjectsBanner
 * @property {GiveawaySweepstakesContent} sweepstakes
 */

/** @type {FamilyVacation2026Config} */
const config = {
	disclaimer: {
		bottomBgImg: {
			base: '/assets-mkt/routes/home/white_squiggle_up_v2.svg',
		},
		bottomBlueBgImg: {
			base: '/assets-mkt/routes/home/blue_squiggle_v2.svg',
		},
		disclaimerParagraph1:
			'The ABCmouse Family Vacation Giveaway is not affiliated with, endorsed, or sponsored by Disney Enterprises, Inc., or any of their subsidiaries or affiliates. Disneyland®, Disneyland California Adventure®, Park Hopper® are registered trademarks of Disney Enterprises, Inc. and its affiliates.',
		disclaimerParagraph2:
			'NO PURCHASE NECESSARY. The ABCmouse Family Vacation Giveaway is open only to legal residents of the 50 United States (and DC), who are at least 18 years old at the time of entry. Giveaway starts on April 15, 2026 at 12:01 a.m. Pacific Time ("PT") and ends on May 31, 2026 at 11:59 p.m. PT. For Official Rules, <a class="text-inherit underline" data-track-id="abc-giveaway-family-vacation-2026:bottom:sweepstake-rules" href="/assets-mkt/routes/giveaway/family-vacation-2026/official-rules.pdf" target="_blank" rel="noopener noreferrer">click here</a>. Void where prohibited. Sponsored by Age of Learning, Inc., 101 N. Brand Blvd., Suite 700 Glendale, CA 91203.',
	},
	footer: homePageConfig.footer,
	headerLinkTrackIds: {
		giveAGift: 'abc-giveaway-family-vacation-2026:header-bar:give-a-gift',
		login: 'abc-giveaway-family-vacation-2026:header-bar:login',
		logout: 'abc-giveaway-family-vacation-2026:header-bar:logout',
		subscribe: 'abc-giveaway-family-vacation-2026:header-bar:subscribe',
	},
	heroCallToAction: {
		abcmouseLogoSweepstakes: {
			alt: 'Family Vacation Giveaway',
			sources: [
				{
					srcset: '/assets-mkt/routes/giveaway/family-vacation-2026/family-vacation-giveaway.svg',
				},
			],
		},
		component: HeroCallToAction,
		ctaButtonText: 'Subscribe to Enter!',
		ctaButtonTrackId: 'abc-giveaway-family-vacation-2026:header-image:subscribe',
		subtext: 'Please see details below.',
		subtitle: 'Enter for a chance to win a trip<br />to Disneyland® for a family of six.',
		title: 'Family Vacation Giveaway',
	},
	joinCta: {
		ctaButtonText: 'Subscribe to Enter!',
		ctaButtonTrackId: 'abc-giveaway-family-vacation-2026:bottom:subscribe',
	},
	learningMeetsAdventure: {
		topBgImg: {
			base: '/assets-mkt/routes/giveaway/family-vacation-2026/wave-f5f5f5.svg',
		},
		learningMeetsAdventureLogo: {
			alt: 'Learning Meets Adventure',
			sources: [
				{
					srcset: '/assets-mkt/routes/giveaway/family-vacation-2026/learning-meets-adventure.svg',
				},
			],
		},
		paragraph:
			'ABCmouse helps families build meaningful moments every day through learning and play. Our Family Vacation Giveaway is one more way we’re celebrating the joy of exploring and making lasting memories together.',
	},
	offerSpecifics: {
		items: [
			{
				body: {
					type: 'paragraphettes',
					items: [
						'One winner plus five guests will receive the following:',
						'<strong>2-Day Park Hopper® Tickets</strong> to Disneyland® and Disney<br class="hidden md:block" /> California Adventure® Parks',
						'<strong>Round-trip flights</strong> from anywhere in the U.S.<br class="md:hidden block" /> (including HI & AK)',
						'<strong>4 days / 3 nights</strong> at a hotel near Disneyland®',
						'<strong>One-day VIP guided tour</strong>—skip the lines and maximize your day',
						'<strong>ABCmouse gift bag</strong> filled with fun surprises',
						'<strong>$4,000 spending money</strong> for meals, souvenirs, and more',
					],
				},
				headline: 'What’s Included?',
				img: {
					alt: 'What is Included',
					sources: [
						{
							srcset: '/assets-mkt/routes/giveaway/family-vacation-2026/abc-with-park.png',
						},
					],
				},
				imgClass:
					'mb-[2.8rem] w-full max-w-[36.7rem] object-contain object-center sm:mb-[3.5rem] sm:max-w-[50.9rem] md:mb-0 md:max-w-[49.3rem] md:shrink-0 lg:max-w-[58.5rem]',
			},
			{
				body: {
					type: 'paragraphBullets',
					bullets: [
						'Limit one <strong>(1)</strong> entry per person. Promotion ends May 31, 2026 at 11:59 PM PT.',
						'No purchase necessary.<br class="hidden sm:inline md:hidden" /> See <a class="text-inherit underline" data-track-id="abc-giveaway-family-vacation-2026:sweepstake-rules" href="/assets-mkt/routes/giveaway/family-vacation-2026/official-rules.pdf" target="_blank" rel="noopener noreferrer">Official Rules</a> for details on an alternate method of entry, including how current subscribers may enter.',
					],
					paragraph:
						'Subscribe to any ABCmouse plan between April 15, 2026 and May 31, 2026 and you’ll be automatically entered for a chance to win.',
				},
				headline: 'How to Enter',
				img: {
					alt: 'How to Enter',
					sources: [
						{
							srcset: '/assets-mkt/routes/giveaway/family-vacation-2026/123-abc-melody-park.png',
						},
					],
				},
				imgClass:
					'mb-[2.8rem] w-full max-w-[30.9rem] object-contain object-center sm:mb-[3.5rem] sm:max-w-[35.1rem] md:mb-0 md:max-w-[39.9rem] lg:max-w-[36.2rem]',
			},
			{
				body: {
					type: 'paragraph',
					text: 'The winner will be selected in a random drawing in June 2026 from all eligible entries received.',
				},
				headline: 'Winner Selection',
				img: {
					alt: 'Winner Selection',
					sources: [
						{
							srcset: '/assets-mkt/routes/giveaway/family-vacation-2026/ivan-doremi-park.png',
						},
					],
				},
				imgClass:
					'mb-[2.8rem] w-full max-w-[37.1rem] object-contain object-center sm:mb-[3.5rem] sm:max-w-[58.3rem] md:mb-0 md:max-w-[64.4rem] lg:max-w-[66.3rem]',
			},
		],
		subtitle: 'Make magical family memories—on us.',
		title: 'Your Family’s<br class="sm:hidden" /> Disneyland® Adventure Awaits.',
	},
	metadata: {
		link: [
			{
				href: 'https://www.abcmouse.com/abc/giveaway/family-vacation-2026/',
				rel: 'canonical',
			},
		],
		metaTag: [
			{
				content:
					'Enter the ABCmouse Family Vacation Giveaway for a chance to win a fun family getaway. Discover how to enter, eligibility details, official rules, and entry deadlines.',
				name: 'description',
			},
			{
				content: 'summary_large_image',
				name: 'twitter:card',
			},
			{
				content:
					'Enter the ABCmouse Family Vacation Giveaway for a chance to win a fun family getaway. Read the official details and submit your entry today.',
				name: 'twitter:description',
			},
			{
				content:
					'https://www.abcmouse.com/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-large-v2.png',
				name: 'twitter:image',
			},
			{
				content: 'ABCmouse Family Vacation Giveaway | Enter for a Chance to Win',
				name: 'twitter:title',
			},
			{
				content:
					'Enter the ABCmouse Family Vacation Giveaway for a chance to win a fun family getaway. See how to enter, eligibility details, official rules, and deadlines.',
				property: 'og:description',
			},
			{
				content:
					'https://www.abcmouse.com/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-large-v2.png',
				property: 'og:image',
			},
			{
				content: 'ABCmouse Family Vacation Giveaway promotional image',
				property: 'og:image:alt',
			},
			{
				content: 'en_US',
				property: 'og:locale',
			},
			{
				content: 'ABCmouse',
				property: 'og:site_name',
			},
			{
				content: 'ABCmouse Family Vacation Giveaway | Enter for a Chance to Win',
				property: 'og:title',
			},
			{
				content: 'website',
				property: 'og:type',
			},
			{
				content: 'https://www.abcmouse.com/abc/giveaway/family-vacation-2026/',
				property: 'og:url',
			},
		],
		schemaOrg: {
			graph: {
				additionalGraphItems: [
					{
						'@context': 'https://schema.org',
						'@type': 'Event',
						'description':
							'A promotional sweepstakes offering eligible entrants a chance to win a family vacation.',
						'endDate': '2026-05-31T23:59:59-07:00',
						'eventAttendanceMode': 'https://schema.org/OnlineEventAttendanceMode',
						'eventStatus': 'https://schema.org/EventScheduled',
						'name': 'ABCmouse Family Vacation Giveaway',
						'organizer': {
							'@type': 'Organization',
							'name': 'ABCmouse',
							'url': 'https://www.abcmouse.com',
						},
						'startDate': '2026-04-15T00:00:00-07:00',
						'url': 'https://www.abcmouse.com/abc/giveaway/family-vacation-2026/',
					},
				],
				webPage: {
					description:
						'Enter the ABCmouse Family Vacation Giveaway for a chance to win a family vacation. Learn how to enter, review eligibility requirements, and read the official rules.',
					id: 'https://www.abcmouse.com/abc/giveaway/family-vacation-2026/#webpage',
					inLanguage: 'en-US',
					isPartOf: {
						id: 'https://www.abcmouse.com/#website',
					},
					name: 'ABCmouse Family Vacation Giveaway',
					primaryImageOfPage: {
						type: 'ImageObject',
						url: 'https://www.abcmouse.com/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-large-v2.png',
					},
					publisher: {
						id: 'https://www.abcmouse.com/#organization',
					},
					url: 'https://www.abcmouse.com/abc/giveaway/family-vacation-2026/',
				},
			},
		},
		title: 'ABCmouse Family Vacation Giveaway | Enter for a Chance to Win',
	},
	premiumValueProps: {
		items: [
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/library_v3.png' }],
					alt: 'Premium Feature 1',
				},
				text: 'Unlimited access to our complete<br class="md:hidden" /> library of over 13,000 activities',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/child_profiles_v3.png' }],
					alt: 'Premium Feature 2',
				},
				text: 'Up to three<br class="hidden md:block" /> child profiles',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/bot_beat_music_v3.png' }],
					alt: 'Premium Feature 3',
				},
				text: 'All curriculum subjects including<br class="sm:hidden" /> Science, Coding, Art & Music, and more',
			},
			{
				img: {
					sources: [{ srcset: '/assets-mkt/routes/home/content_v3.png' }],
					alt: 'Premium Feature 4',
				},
				text: 'New content added <br /> all the time',
			},
		],
		title: 'ABCmouse includes:',
	},
	subjectsBanner: {
		subjects: ['Reading', 'Math', 'Science', 'Art & Colors', 'Music'],
	},
	sweepstakes: {
		// Three raster tiers (small / medium / large). No separate sm URL: 640–1199px uses `base` per ImageService.getLastMatchingSource.
		sweepstakesBg: {
			base: '/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-small-v2.png',
			sm: '/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-medium-v2.png',
			md: '/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-large-v2.png',
			lg: '/assets-mkt/routes/giveaway/family-vacation-2026/roller-coaster-large-v2.png',
		},
	},
};

export default config;
